/**
 * Conversation Outcome Config Schema
 *
 * Stored in SystemConfig under category="conversation", one row:
 *   - key="outcome" → drives AI-containment capture
 *
 * An OPEN conversation with no human handoff that stays idle past
 * `idleTimeoutMinutes` is recorded as AI-contained, feeding the
 * analytics containment funnel.
 *
 * The setting is system-wide (not per-org).
 *
 * The three bounds below are the single source for every consumer: the
 * validator that accepts an admin edit, the code default the classifier
 * falls back to, and the admin form that renders the allowed range. A form
 * that hard-coded its own copy would keep claiming "5 … 43200" after the
 * validator moved, which is precisely the class of drift this file exists
 * to prevent.
 */

import { z } from "zod";

/** Shortest accepted idle threshold — below this, a live conversation reads as concluded. */
export const IDLE_TIMEOUT_MINUTES_MIN = 5;

/** Longest accepted idle threshold: 30 days. */
export const IDLE_TIMEOUT_MINUTES_MAX = 43200;

/** Code default: 24 hours. Used when the SystemConfig row is absent or unparseable. */
export const IDLE_TIMEOUT_MINUTES_DEFAULT = 1440;

export const ConversationOutcomeConfigSchema = z.object({
  /** Idle minutes after which an OPEN, un-escalated conversation is recorded as AI-contained. */
  idleTimeoutMinutes: z.number().int().min(IDLE_TIMEOUT_MINUTES_MIN).max(IDLE_TIMEOUT_MINUTES_MAX)
});

export type ConversationOutcomeConfig = z.infer<typeof ConversationOutcomeConfigSchema>;
